import { defineStore } from 'pinia'
import { useUiStore } from '@/stores/ui/store'
import { useNetworkStore } from '@/stores/network/store'
import * as Api from './api'
import {
  createMetaOld,
  getMetaErrorOld,
  getMetaPendingOld,
  getMetaSuccessOld,
  type MetaOld,
} from '@/utils/store'
import type { ExportFileType } from '@/components/uca/contacts/types'
import { useNotificationStore } from '../notifications/store'
import { selectNotification } from '../notifications/utils'

export type Contact = {
  id: number
  firstname: string
  lastname: string
  org: string
  phones: [
    {
      contactId: number
      id: number
      phone: string
      phoneParamType: 'phone' | 'office' | 'private' | 'mobile' | 'fax'
    },
  ]
  emails: [
    {
      contactId: number
      email: string
      emailParamType: 'office' | 'private'
      id: number
    },
  ]
  addresses: [
    {
      addressParamType: 'office' | 'private'
      city: string
      country: string
      extended: string
      id: number
      street: string
      zip: string
    },
  ]
  urls?: any[]
  userId: number
}

type FilterParams = {
  page: number
  org: string
  searchParameter: string
  perPage: number
  orderKey: 'firstname' | 'lastname' | 'org'
  orderValue: 'asc' | 'desc'
}

type State = {
  byId: Record<string, Contact>
  allIds: number[]
  filterParams: FilterParams
  selectedOrgs: string[]
  lastPage: number
  total: number
  orgs: string[]
  selectedContacts: any[]
  selectAll: boolean
  contactsSettings: any[]
  openedPanel: any
  destination: any
  importErrors: string[]
  meta: MetaOld<
    | 'load'
    | 'create'
    | 'update'
    | 'delete'
    | 'dial'
    | 'updateContactsSettings'
    | 'importContacts'
    | 'exportContacts'
  >
}

const getDefaultState = (): State => ({
  byId: {},
  allIds: [],
  filterParams: {
    page: 1,
    org: '',
    searchParameter: '',
    perPage: 25,
    orderKey: 'firstname',
    orderValue: 'asc',
  },
  selectedOrgs: [],
  lastPage: 1,
  total: 0,
  orgs: [],
  selectedContacts: [],
  selectAll: false,
  contactsSettings: [],
  openedPanel: null,
  destination: null,
  importErrors: [],
  meta: createMetaOld([
    'load',
    'create',
    'update',
    'delete',
    'dial',
    'updateContactsSettings',
    'importContacts',
    'exportContacts',
  ]),
})

export const useContactsStore = defineStore('contacts', {
  state: getDefaultState,

  getters: {
    find: (state) => (id: number) => state.byId[id],
    list(state): Contact[] {
      return state.allIds.map((id) => this.find(id))
    },
  },

  actions: {
    RESET() {
      this.byId = {}
      this.allIds = []
    },

    ADD(item: Contact) {
      this.byId[item.id] = item

      if (this.allIds.includes(item.id)) {
        return
      }

      this.allIds.push(item.id)
    },

    DELETE(contactId: number) {
      delete this.byId[contactId]

      this.allIds = this.allIds.filter((id) => id !== contactId)
    },

    SET_FILTER_PARAMS(filterParams: Partial<FilterParams>) {
      this.filterParams = {
        ...this.filterParams,
        ...filterParams,
      }
    },

    SET_SELECTED_ORGS(orgs: string[]) {
      this.selectedOrgs = orgs
    },

    SET_LAST_PAGE(lastPage: number) {
      this.lastPage = lastPage
    },

    SET_TOTAL(total: number) {
      this.total = total
    },

    SET_ORGS(orgs: string[]) {
      this.orgs = orgs
    },

    SET_SELECTED_CONTACTS(selectedContacts: any[]) {
      this.selectedContacts = selectedContacts
    },

    SET_SELECT_ALL(selectAll: boolean) {
      this.selectAll = selectAll
    },

    SET_CONTACTS_SETTINGS(contactsSettings: any[]) {
      this.contactsSettings = contactsSettings
    },

    SET_OPENED_PANEL(openedPanel: any) {
      this.openedPanel = openedPanel
    },

    SET_DESTINATION(destination: any) {
      this.destination = destination
    },

    async load() {
      this.meta.load = getMetaPendingOld()

      useUiStore().startBlockingActions()

      const resultLoadContacts =
        this.selectedOrgs.length > 1
          ? await this.loadSelectedOrgs()
          : await Api.loadContacts(this.filterParams)

      if (resultLoadContacts.status === 'error') {
        useNetworkStore().HANDLE_LOADING_ERROR(resultLoadContacts.payload)
      }

      const contacts = resultLoadContacts.payload

      const resultLoadContactsSettings = await Api.loadContactsSettings()

      if (resultLoadContactsSettings.status === 'error') {
        this.meta.load = getMetaErrorOld()

        throw resultLoadContactsSettings.payload
      }

      const contactsSettings = resultLoadContactsSettings.payload

      this.SET_SELECTED_CONTACTS([])

      this.SET_SELECT_ALL(false)

      this.RESET()

      contacts.data.forEach((item: Contact) => {
        this.ADD(item)
      })

      this.SET_TOTAL(contacts.total)

      this.SET_LAST_PAGE(contacts.last_page)

      this.SET_ORGS(contacts.orgs)

      this.SET_CONTACTS_SETTINGS(contactsSettings)

      useUiStore().stopBlockingActions()

      this.meta.load = getMetaSuccessOld()
    },

    async loadSelectedOrgs() {
      const results = await Promise.all(
        this.selectedOrgs.map(async (org) => {
          const params = { ...this.filterParams, org, page: 1 }
          const firstPage = await Api.loadContacts(params)

          if (firstPage.status === 'error') {
            return { status: 'error' as const, payload: firstPage.payload }
          }

          const remainingPages = await Promise.all(
            Array.from(
              { length: Math.max(0, firstPage.payload.last_page - 1) },
              (_, index) => Api.loadContacts({ ...params, page: index + 2 })
            )
          )

          const failedPage = remainingPages.find(
            (page) => page.status === 'error'
          )
          if (failedPage) {
            return { status: 'error' as const, payload: failedPage.payload }
          }

          return {
            status: 'success' as const,
            payload: {
              orgs: firstPage.payload.orgs,
              data: [
                ...firstPage.payload.data,
                ...remainingPages.flatMap((page) => page.payload.data),
              ] as Contact[],
            },
          }
        })
      )

      const failedResult = results.find((result) => result.status === 'error')
      if (failedResult) {
        return failedResult
      }

      const { orderKey, orderValue, page, perPage } = this.filterParams
      const successfulResults = results.filter(
        (result) => result.status === 'success'
      )
      const data: Contact[] = successfulResults
        .flatMap((result) => result.payload.data)
        .sort((a, b) => {
          const comparison = (a[orderKey] ?? '').localeCompare(
            b[orderKey] ?? ''
          )
          if (comparison) {
            return orderValue === 'desc' ? -comparison : comparison
          }

          return a.id - b.id
        })

      return {
        status: 'success' as const,
        payload: {
          data: data.slice((page - 1) * perPage, page * perPage),
          total: data.length,
          last_page: Math.max(1, Math.ceil(data.length / perPage)),
          orgs: successfulResults[0].payload.orgs,
        },
      }
    },

    async create(contactData: any) {
      this.meta.create = getMetaPendingOld()

      const result = await Api.createContact(contactData)

      if (result.status === 'error') {
        this.meta.create = getMetaErrorOld()

        throw result.payload
      }

      this.meta.create = getMetaSuccessOld()

      this.load()
    },

    async update({ id, contactData }: { id: number; contactData: any }) {
      this.meta.update = getMetaPendingOld()

      const result = await Api.updateContact(id, contactData)

      if (result.status === 'error') {
        this.meta.update = getMetaErrorOld()

        throw result.payload
      }

      this.meta.update = getMetaSuccessOld()

      this.load()
    },

    async delete() {
      this.meta.delete = getMetaPendingOld()

      const { org, searchParameter } = this.filterParams

      const destroyParams: {
        contactIds?: number[]
        destroyAll?: boolean
        org?: string
        searchParameter?: string
      } = {}

      if (this.selectAll) {
        if (org || searchParameter) {
          if (searchParameter) {
            destroyParams.searchParameter = searchParameter
          }

          if (org) {
            destroyParams.org = org
          }
        } else {
          destroyParams.destroyAll = true
        }
      } else {
        destroyParams.contactIds = this.selectedContacts.reduce(
          (contactIds, contactId) => {
            contactIds.push(contactId)
            return contactIds
          },
          []
        )
      }

      const result = await Api.destroyContacts(destroyParams)

      if (result.status === 'error') {
        this.meta.delete = getMetaErrorOld()

        throw result.payload
      }

      this.meta.delete = getMetaSuccessOld()

      this.load()
    },

    async deleteUca(contactIds: number[], { selectAll = false } = {}) {
      this.meta.delete = getMetaPendingOld()

      const { org, searchParameter } = this.filterParams

      const destroyParams: Parameters<typeof Api.destroyContacts>[0] =
        selectAll ? {} : { contactIds }

      if (selectAll) {
        if (org) {
          destroyParams.org = org
        }

        if (searchParameter) {
          destroyParams.searchParameter = searchParameter
        }

        if (!org && !searchParameter) {
          destroyParams.destroyAll = true
        }
      }

      const result = await Api.destroyContacts(destroyParams)

      if (result.status === 'error') {
        this.meta.delete = getMetaErrorOld()

        useNotificationStore().add(selectNotification(result.payload))
      } else {
        this.meta.delete = getMetaSuccessOld()

        this.load()
      }
    },

    async dial(dialParams: any) {
      this.meta.dial = getMetaPendingOld()

      const result = await Api.dialContact(dialParams)

      if (result.status === 'error') {
        this.meta.dial = getMetaErrorOld()

        throw result.payload
      }

      this.meta.dial = getMetaSuccessOld()
    },

    async updateContactsSettings() {
      this.meta.updateContactsSettings = getMetaPendingOld()

      const result = await Api.updateContactsSettings(this.contactsSettings)

      if (result.status === 'error') {
        this.meta.updateContactsSettings = getMetaErrorOld()

        throw result.payload
      }

      this.meta.updateContactsSettings = getMetaSuccessOld()
    },

    async importContacts(importFile: any) {
      this.meta.importContacts = getMetaPendingOld()
      this.importErrors = []

      const formData = new FormData()

      formData.append('file', importFile)

      const result = await Api.importContacts(formData)

      if (result.status === 'error') {
        this.meta.importContacts = getMetaErrorOld()

        const backendErrors = (result.payload as any)?.response?.data?.errors

        this.importErrors = backendErrors
          ? Object.values(backendErrors).map((messages: any) => messages[0])
          : []

        throw result.payload
      } else {
        this.meta.importContacts = getMetaSuccessOld()

        this.load()
      }
    },

    async exportContacts(format: 'csv' | 'vcard') {
      this.meta.exportContacts = getMetaPendingOld()

      const { org, searchParameter } = this.filterParams

      const exportParams: {
        format: 'text/csv' | 'text/vcard'
        contactIds?: number[]
        org?: string
        searchParameter?: string
      } = { format: `text/${format}` }

      if (this.selectAll) {
        if (searchParameter) {
          exportParams.searchParameter = searchParameter
        }

        if (org) {
          exportParams.org = org
        }
      } else {
        exportParams.contactIds = this.selectedContacts.reduce(
          (contactIds, contactId) => {
            contactIds.push(contactId)
            return contactIds
          },
          []
        )
      }

      const result = await Api.exportContacts(exportParams)

      if (result.status === 'error') {
        this.meta.exportContacts = getMetaErrorOld()

        useNetworkStore().HANDLE_LOADING_ERROR(result.payload)
      }

      this.meta.exportContacts = getMetaSuccessOld()

      return result.payload
    },

    async exportContactsUca(
      contactIds: number[],
      fileType: ExportFileType,
      { selectAll = false } = {}
    ) {
      this.meta.exportContacts = getMetaPendingOld()

      const { org, searchParameter } = this.filterParams

      const exportParams: Parameters<typeof Api.exportContacts>[0] = {
        format: `text/${fileType}`,
      }

      if (selectAll) {
        if (org) {
          exportParams.org = org
        }

        if (searchParameter) {
          exportParams.searchParameter = searchParameter
        }
      } else {
        exportParams.contactIds = contactIds
      }

      const result = await Api.exportContacts(exportParams)

      if (result.status === 'error') {
        this.meta.exportContacts = getMetaErrorOld()

        useNetworkStore().HANDLE_LOADING_ERROR(result.payload)
      } else {
        const fileExtension = fileType === 'vcard' ? 'vcf' : 'csv'

        const fileURL = window.URL.createObjectURL(result.payload)

        const fileLink = document.createElement('a')

        fileLink.href = fileURL

        fileLink.setAttribute('download', `kontakte.${fileExtension}`)

        document.body.appendChild(fileLink)

        fileLink.click()

        document.body.removeChild(fileLink)

        window.URL.revokeObjectURL(fileURL)

        this.meta.exportContacts = getMetaSuccessOld()
      }
    },

    setFilterParam(filterParam: Partial<FilterParams>) {
      if ('org' in filterParam) {
        this.SET_SELECTED_ORGS(filterParam.org ? [filterParam.org] : [])
      }

      this.SET_FILTER_PARAMS(filterParam)

      if (!filterParam.page) {
        this.SET_FILTER_PARAMS({ page: 1 })
      }

      this.load()
    },

    setSelectedOrgs(orgs: string[]) {
      this.SET_SELECTED_ORGS(orgs)
      this.SET_FILTER_PARAMS({ org: orgs.length === 1 ? orgs[0] : '', page: 1 })
      this.load()
    },

    setSelectedContacts(selectedContacts: any[]) {
      this.SET_SELECTED_CONTACTS(selectedContacts)
    },

    setSelectAll(selectAll: boolean) {
      this.SET_SELECT_ALL(selectAll)
    },

    setContactsSettings(contactsSettings: any[]) {
      this.SET_CONTACTS_SETTINGS(contactsSettings)
    },

    setOpenedPanel(openedPanel: string | null) {
      this.SET_OPENED_PANEL(openedPanel)
    },

    setDestination(destination: string) {
      this.SET_DESTINATION(destination)
    },
  },
})
